/**
 * Chrome component wraps main application content
 * at the top-most level
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { parse } from '@exo/frontend-common-utils';
import {
  useTranslations,
  useAppShellContext
} from '@exo/frontend-common-app-shell';
import CookieBanner from './CookieBanner';
import * as S from './Chrome.styles';

const Chrome = ({
  children,
  cookieBannerButtonId,
  cookieBannerRejectButtonId,
  cookieBannerLinkId,
  cookieBannerTextId,
  cookieBannerTitleId,
  footer,
  header,
  isAuth,
  notifications,
  cookieTranslationId
}) => {
  // This block checks whether the root app is Fastlane or not
  const { config } = useAppShellContext();
  const cookieConsent = parse(window?.localStorage?.cookieConsent);

  // Passed Indirect prop and checking it here to ensure Cookie pref prompt is not shown for (Fastlane)
  const [showCookieBanner, setShowCookieBanner] = useState(
    !cookieConsent?.hasAskedCookieConsent && config?.requiresCookieConsent
  );

  const { pathname } = useLocation();
  const isCookiePage = pathname.includes('manage-cookie');

  // Note: this requires the component to be wrapped by ApolloProvider
  const { messages } = useTranslations(cookieTranslationId);

  const handleStorageEvent = e => {
    const newLocalStorageValue = parse(e.newValue);
    const hasAskedCookieConsent =
      newLocalStorageValue?.cookieConsent?.hasAskedCookieConsent &&
      config?.requiresCookieConsent;

    if (hasAskedCookieConsent) setShowCookieBanner(false);
  };
  useEffect(() => {
    window.addEventListener('storage', handleStorageEvent);
    return () => {
      window.removeEventListener('storage', handleStorageEvent);
    };
  });
  useEffect(() => {
    if (isCookiePage) {
      setShowCookieBanner(false);
    } else {
      setShowCookieBanner(
        !cookieConsent?.hasAskedCookieConsent && config?.requiresCookieConsent
      );
    }
  }, [isCookiePage, cookieConsent, config]);

  return (
    <S.Chrome>
      {showCookieBanner && (
        <CookieBanner
          title={messages?.[cookieBannerTitleId]}
          text={messages?.[cookieBannerTextId]}
          buttonText={messages?.[cookieBannerButtonId]}
          rejectButtonText={messages?.[cookieBannerRejectButtonId]}
          linkText={messages?.[cookieBannerLinkId]}
          isAuth={isAuth}
          setShowCookieBanner={setShowCookieBanner}
        />
      )}
      <S.Page>
        <S.Overlay isVisible={showCookieBanner} />
        {header}
        {notifications && (
          <S.Notifications className="notifications-wrapper">
            {notifications}
          </S.Notifications>
        )}
        <S.Main>{children}</S.Main>
        {footer}
      </S.Page>
    </S.Chrome>
  );
};

Chrome.defaultProps = {
  cookieBannerButtonId: 'cookieManagement.cookieBannerButton',
  cookieBannerLinkId: 'cookieManagement.cookieBannerLink',
  cookieBannerTextId: 'cookieManagement.cookieBannerText',
  cookieBannerTitleId: 'cookieManagement.cookieBannerTitle',
  footer: null,
  header: null,
  isAuth: false,
  notifications: null,
  cookieTranslationId: 'cookieManagement'
};

Chrome.propTypes = {
  children: PropTypes.node.isRequired,
  cookieBannerButtonId: PropTypes.string,
  cookieBannerRejectButtonId: PropTypes.string,
  cookieBannerLinkId: PropTypes.string,
  cookieBannerTextId: PropTypes.string,
  cookieBannerTitleId: PropTypes.string,
  footer: PropTypes.node,
  header: PropTypes.node,
  isAuth: PropTypes.bool,
  notifications: PropTypes.node,
  cookieTranslationId: PropTypes.string
};

export default Chrome;
